// MessageList.tsx
import React, { RefObject } from "react";
import MessageBubble from "./MessageBubble";
import ChipList, { ChipType } from "./ChipList";
import Table, { TableType } from "./Table"; // Import new Table component
import * as S from "./ChatInterfaceStyles";
import smallLogo from "./smalllogo.png";
import DynamicList from "./DynamicList";
import DynamicForm from "./DynamicForm";
import Cards from "./Cards";
import AgentTransferDivider from "./AgentTransferDivider";
import { ListType, FormSpec, CardSpec } from "./MessagePreprocessor";

export interface Attachment {
  id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  file_url: string;
  preview_url?: string;
}

interface Message {
  sender: string;
  content: string;
  chips?: ChipType[];
  table?: TableType; // Add table
  pdf_path?: string;
  docx_path?: string;
  recipient_email?: string;
  type?: "transfer";
  fromAgent?: string;
  toAgent?: string;
  transferState?: "transferring" | "connected";
  image_path?: string;
  attachments?: Attachment[];
  folder_info?: {
    name: string;
    file_count: number;
  };
}

interface MessageListProps {
  messages: Message[];
  preprocessMessage: (msg: Message) => {
    displayContent: string;
    chips: ChipType[] | undefined;
    table?: TableType; // Add table
    list?: ListType;
    form?: FormSpec;
    cards?: CardSpec[];
    pdf_path?: string;
    docx_path?: string;
    recipient_email?: string;
  };
  onChipClick: (chip: ChipType, message: Message) => void;
  onFormSubmit?: (values: Record<string, any>, sourceMessage: Message) => void;
  onCardAction?: (action: { label: string; action: string; params?: Record<string, any> }, card: CardSpec, sourceMessage: Message) => void;
  isLoading: boolean;
  lastMessageRef: RefObject<HTMLDivElement | null>;
  onScheduleInterview?: (candidateName: string) => void; // Add this prop
}

const MessageList: React.FC<MessageListProps> = ({
  messages,
  preprocessMessage,
  onChipClick,
  onFormSubmit,
  onCardAction,
  isLoading,
  lastMessageRef,
  onScheduleInterview, // Destructure the new prop
}) => {
  const lastMessage = messages[messages.length - 1];
  const isLastMessageFromUser = lastMessage && lastMessage.sender === "You";

  const dedupeChips = (chips: ChipType[] | undefined): ChipType[] => {
    if (!chips || chips.length === 0) return [];
    const seen = new Set<string>();
    return chips.filter((chip) => {
      const key = `${chip.label}::${chip.action}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  // Filter out consecutive duplicate messages from the agent
  const filteredMessages = messages.filter((message, index) => {
    if (index === 0) return true;
    const prevMessage = messages[index - 1];
    // Check for agent messages with identical content
    if (
      message.sender !== "You" &&
      prevMessage.sender !== "You" &&
      message.content === prevMessage.content &&
      message.content.includes("Here are the candidates with skills in :")
    ) {
      return false; // Filter out this duplicate message
    }
    return true;
  });

  // Keep transfer messages so they can render AgentTransferDivider.
  const effectiveMessages = filteredMessages;

  const preprocessedMessages = effectiveMessages.map((message) => ({
    message,
    parsed: preprocessMessage(message),
  }));

  const lastMessageWithChipsIndex = (() => {
    for (let i = preprocessedMessages.length - 1; i >= 0; i -= 1) {
      const entry = preprocessedMessages[i];
      if (entry.message.sender === "You") continue;
      const uniqueChips = dedupeChips(entry.parsed.chips);
      if (uniqueChips.length > 0) return i;
    }
    return -1;
  })();

  return (
    <>
      {preprocessedMessages.map(({ message, parsed }, index) => {
        const isTransfer = (message as any).type === "transfer";
        if (isTransfer) {
          const state =
            (message as any).transferState === "connected"
              ? "connected"
              : "transferring";
          const fromAgent =
            (message as any).fromAgent || message.sender || "Agent";
          let toAgent =
            (message as any).toAgent || (message.content as string) || "agent";
          if (typeof toAgent === "string") {
            toAgent = toAgent
              .replace(/^Transferring to\s+/i, "")
              .replace(/^Connected to\s+/i, "")
              .replace(/\.\s*$/, "");
          }

          return (
            <AgentTransferDivider
              key={index}
              fromAgent={fromAgent}
              toAgent={toAgent}
              state={state}
            />
          );
        }

        const isUser = message.sender === "You";
        const { displayContent, chips, table, list, form, cards } = parsed;
        const uniqueChips = dedupeChips(chips);
        const shouldRenderChips = !isUser && index === lastMessageWithChipsIndex && uniqueChips.length > 0;

        return (
          <S.MessageRow
            key={index}
            $isUser={isUser}
            ref={index === preprocessedMessages.length - 1 ? lastMessageRef : null}
          >
            {/* {!isUser && (
              <S.Avatar $isUser={isUser}>
                <S.AvatarImage src={smallLogo} alt="Agent Logo" />
              </S.Avatar>
            )} */}

            <S.BubbleWrapper $isUser={isUser}>
              <MessageBubble
                content={displayContent}
                isUser={isUser}
                image_path={message.image_path}
                attachments={message.attachments}
                folder_info={message.folder_info}
              />

              {/* Chips */}
              {shouldRenderChips && (
                <ChipList chips={uniqueChips} onChipClick={(chip) => onChipClick(chip, message)} />
              )}

              {/* Table */}
              {!isUser && table && (
                <Table table={table} onScheduleInterview={onScheduleInterview} />
              )}

              {/* List */}
              {!isUser && list && <DynamicList list={list} />}

              {/* Form */}
              {!isUser && form && (
                <DynamicForm form={form} onSubmit={(vals) => onFormSubmit && onFormSubmit(vals, message)} />
              )}

              {/* Cards */}
              {!isUser && cards && cards.length > 0 && (
                <Cards
                  cards={cards}
                  onAction={(a, c) => {
                    if (a.label === "Schedule" && onScheduleInterview) {
                      onScheduleInterview(c.title); // Assuming candidate name is in card.title
                    } else if (onCardAction) {
                      onCardAction(a, c, message);
                    }
                  }}
                />
              )}
            </S.BubbleWrapper>

            {/* {isUser && <S.Avatar $isUser={isUser}>U</S.Avatar>} */}
          </S.MessageRow>
        );
      })}

      {isLoading && (messages.length === 0 || isLastMessageFromUser) && (
        <S.MessageRow $isUser={false} ref={lastMessageRef}>
          {/* <S.Avatar $isUser={false}>
            <S.AvatarImage src={smallLogo} alt="Agent Logo" />
          </S.Avatar> */}
          <S.LoaderWrapper>
            <S.Spinner>
              <div />
              <div />
              <div />
              <div />
              <div />
            </S.Spinner>
          </S.LoaderWrapper>
        </S.MessageRow>
      )}
    </>
  );
};

export default MessageList;
